import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  ChevronDown,
  ChevronUp,
  FileText,
  History,
  Loader2,
  Maximize2,
  MessageSquare,
  Minimize2,
  Sparkles,
  Square,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Textarea,
  toast,
} from "@lumanity/ui/primitives";
import { type EvidenceDocument, useEvidenceDocuments, useProjectDocuments } from "@lumanity/ui/evidence";
import { useDocumentViewer } from "@lumanity/ui/viewer";

import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import type { AppliedFilters, ChatCitation, ChatMessage, ChatSession } from "./chatApi";
import { stopStream, useChatStream, useIsStreaming } from "./chatStreamStore";
import {
  CITATION_HREF_PREFIX,
  extractPageNumber,
  findDocBySourcefile,
  formatRelativeAge,
  getAtQuery,
  linkifyCitations,
  parseAtMentionIds,
} from "./citations";
import { useChatSessions, useCreateChatSession, useDeleteChatSession, useSessionMessages, useStreamChatMessage } from "./useChat";

const MAX_FILE_PICKER_RESULTS = 8;
const SCROLL_BOTTOM_THRESHOLD_PX = 80;
// The composer grows with its draft up to about five lines, then scrolls.
const COMPOSER_MAX_HEIGHT_PX = 120;

type DocsByFilename = Map<string, EvidenceDocument>;
type OpenCitation = (citation: ChatCitation) => void;

function senderLabel(message: ChatMessage, currentEmail: string | undefined): string {
  if (message.role === "assistant") return "Assistant";
  if (message.senderEmail && currentEmail && message.senderEmail.toLowerCase() === currentEmail.toLowerCase()) return "You";
  return message.senderEmail ?? "Member";
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// The session last open in a project, so the assistant reopens where the user left it.
const lastSessionKey = (projectId: string) => `pubcreator.chat.${projectId}.session`;

function readLastSession(projectId: string): string | null {
  try {
    return localStorage.getItem(lastSessionKey(projectId));
  } catch {
    return null;
  }
}

function writeLastSession(projectId: string, sessionId: string | null) {
  try {
    if (sessionId) localStorage.setItem(lastSessionKey(projectId), sessionId);
    else localStorage.removeItem(lastSessionKey(projectId));
  } catch {
    // Storage unavailable (private window, blocked site data): reopening falls back to the latest chat.
  }
}

/** The project's documents (project service links + evidence-base metadata). */
function useProjectEvidence(projectId: string) {
  const projectDocs = useProjectDocuments(projectId);
  const evidence = useEvidenceDocuments();
  const documents = useMemo(() => {
    const byId = new Map((evidence.data ?? []).map((d) => [d.id, d]));
    return (projectDocs.data?.documentIds ?? [])
      .map((id) => byId.get(id))
      .filter((d): d is EvidenceDocument => !!d)
      .sort((a, b) => (a.title || a.filename).localeCompare(b.title || b.filename));
  }, [projectDocs.data, evidence.data]);
  return {
    documents,
    documentIds: projectDocs.data?.documentIds ?? [],
    isLoading: projectDocs.isLoading || evidence.isLoading,
    error: projectDocs.error ?? evidence.error,
  };
}

interface EvidenceChatProps {
  projectId: string;
  /** Close the assistant window (the header's X); omitted, no close button. */
  onClose?: () => void;
  /** Widen or restore the assistant window; omitted, no expand button. */
  onToggleExpand?: () => void;
  expanded?: boolean;
}

/**
 * Evidence Chat (FRD Stage 3): grounded, cited answers over the project's whole
 * Evidence Base (an @-mention narrows one question to the mentioned files),
 * shared sessions, and citations that open the source viewer. Laid out for
 * the workspace's floating assistant window: the conversation, or the chat history.
 *
 * Needs the app-level document viewer (EvidenceViewerProvider from
 * "@/features/evidence") and LumanityProvider above it.
 */
export default function EvidenceChat({ projectId, onClose, onToggleExpand, expanded }: Readonly<EvidenceChatProps>) {
  const { user } = useAuth();
  const { data: sessions = [], isLoading: sessionsLoading, error: sessionsError } = useChatSessions(projectId);
  const createSession = useCreateChatSession(projectId);
  const deleteSession = useDeleteChatSession(projectId);
  const evidence = useProjectEvidence(projectId);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(() => readLastSession(projectId));
  // A new chat is a draft until its first question creates the session (the backend titles it from that question).
  const [isDraft, setIsDraft] = useState(false);
  const [view, setView] = useState<"chat" | "history">("chat");
  const [pendingDelete, setPendingDelete] = useState<ChatSession | null>(null);
  const [firstQuestion, setFirstQuestion] = useState<{ sessionId: string; content: string } | null>(null);

  // Open the last session, else the most recently updated one; reselect when the open one disappears.
  useEffect(() => {
    if (sessionsLoading || isDraft) return;
    if (activeSessionId && sessions.some((s) => s.id === activeSessionId)) return;
    setActiveSessionId(sessions[0]?.id ?? null);
  }, [sessionsLoading, sessions, activeSessionId, isDraft]);

  useEffect(() => {
    if (!sessionsLoading) writeLastSession(projectId, activeSessionId);
  }, [projectId, activeSessionId, sessionsLoading]);

  const activeSession = useMemo(
    () => (isDraft ? null : (sessions.find((s) => s.id === activeSessionId) ?? null)),
    [isDraft, sessions, activeSessionId],
  );

  const documentsByFilename = useMemo<DocsByFilename>(() => {
    const map: DocsByFilename = new Map();
    for (const doc of evidence.documents) if (doc.filename) map.set(doc.filename, doc);
    return map;
  }, [evidence.documents]);

  const openSession = (sessionId: string) => {
    setIsDraft(false);
    setActiveSessionId(sessionId);
    setView("chat");
  };

  const startNewChat = () => {
    setIsDraft(true);
    setActiveSessionId(null);
    setView("chat");
  };

  /** A draft's first question: create the session, then let its view send the question. */
  const askInDraft = async (content: string): Promise<boolean> => {
    try {
      const session = await createSession.mutateAsync({});
      setFirstQuestion({ sessionId: session.id, content });
      openSession(session.id);
      return true;
    } catch (err) {
      toast({ title: "Could not start a chat", description: errorMessage(err), variant: "destructive" });
      return false;
    }
  };

  const confirmDelete = async () => {
    const session = pendingDelete;
    setPendingDelete(null);
    if (!session) return;
    // A reply still streaming into a deleted session has nowhere to land.
    stopStream(session.id);
    try {
      await deleteSession.mutateAsync({ sessionId: session.id });
      if (activeSessionId === session.id) setActiveSessionId(null);
    } catch (err) {
      toast({ title: "Could not delete the chat", description: errorMessage(err), variant: "destructive" });
    }
  };

  const noDocuments = !evidence.isLoading && evidence.documentIds.length === 0;
  const subtitle = view === "history" ? "Chat history" : (activeSession?.title ?? "New chat");

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex items-center gap-2 border-b bg-brand-navy px-3 py-2.5 text-white">
        {view === "history" ? (
          <button
            type="button"
            onClick={() => setView("chat")}
            className="rounded-md p-1.5 hover:bg-white/10"
            aria-label="Back to the conversation"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-teal">
            <Sparkles className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight">AI Assistant</div>
          <div className="truncate text-xs text-white/70">{subtitle}</div>
        </div>
        <HeaderButton label="Chat history" onClick={() => setView((v) => (v === "history" ? "chat" : "history"))} active={view === "history"}>
          <History className="h-4 w-4" />
        </HeaderButton>
        <HeaderButton label="New chat" onClick={startNewChat}>
          <SquarePen className="h-4 w-4" />
        </HeaderButton>
        {onToggleExpand && (
          <HeaderButton label={expanded ? "Shrink" : "Expand"} onClick={onToggleExpand}>
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </HeaderButton>
        )}
        {onClose && (
          <HeaderButton label="Close assistant" onClick={onClose}>
            <X className="h-4 w-4" />
          </HeaderButton>
        )}
      </div>

      {view === "history" ? (
        <SessionList
          sessions={sessions}
          isLoading={sessionsLoading}
          error={sessionsError}
          activeSessionId={activeSession?.id ?? null}
          onSelect={openSession}
          onDelete={setPendingDelete}
          onNewChat={startNewChat}
        />
      ) : activeSession ? (
        // Keyed so per-session state (draft, scroll) never leaks between sessions.
        <ChatSessionView
          key={activeSession.id}
          session={activeSession}
          projectId={projectId}
          currentUserEmail={user?.email}
          documents={evidence.documents}
          totalDocs={evidence.documentIds.length}
          documentsLoading={evidence.isLoading}
          documentsByFilename={documentsByFilename}
          firstQuestion={firstQuestion?.sessionId === activeSession.id ? firstQuestion.content : undefined}
          onFirstQuestionSent={() => setFirstQuestion(null)}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-4">
            {sessionsLoading ? <div className="text-sm text-muted-foreground">Loading…</div> : <Greeting />}
          </div>
          <Composer
            documentsByFilename={documentsByFilename}
            pending={createSession.isPending}
            blockedReason={noDocuments ? "Add documents to the Evidence Base to start asking questions." : null}
            onSend={askInDraft}
          />
        </div>
      )}

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this chat?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingDelete?.title}" and all its messages will be deleted for everyone on the project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HeaderButton({
  label,
  onClick,
  active,
  children,
}: Readonly<{ label: string; onClick: () => void; active?: boolean; children: React.ReactNode }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn("shrink-0 rounded-md p-1.5 text-white/80 hover:bg-white/10 hover:text-white", active && "bg-white/15 text-white")}
    >
      {children}
    </button>
  );
}

const Greeting = () => (
  <div className="rounded-lg border border-brand-teal/15 bg-brand-teal/5 p-4 text-sm">
    <div className="mb-1 font-semibold text-brand-teal">AI Assistant</div>
    <p className="text-foreground">
      Ask about this project's evidence. Answers are grounded in the Evidence Base and cite their sources. Use{" "}
      <span className="font-medium">@</span> to ask about specific documents.
    </p>
  </div>
);

interface SessionListProps {
  sessions: ChatSession[];
  isLoading: boolean;
  error: Error | null;
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onDelete: (session: ChatSession) => void;
  onNewChat: () => void;
}

function SessionList({ sessions, isLoading, error, activeSessionId, onSelect, onDelete, onNewChat }: Readonly<SessionListProps>) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading…</div>
        ) : error ? (
          <div className="p-4 text-sm text-destructive">Could not load chats: {error.message}</div>
        ) : sessions.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No chats yet. Start one to ask questions with your project team.</div>
        ) : (
          <ul className="py-1" aria-label="Chats">
            {sessions.map((session) => (
              <SessionListItem
                key={session.id}
                session={session}
                isActive={session.id === activeSessionId}
                onSelect={() => onSelect(session.id)}
                onDelete={() => onDelete(session)}
              />
            ))}
          </ul>
        )}
      </div>
      <div className="border-t p-3">
        <Button type="button" variant="outline" size="sm" className="w-full gap-1.5" onClick={onNewChat}>
          <SquarePen className="h-4 w-4" /> New chat
        </Button>
      </div>
    </div>
  );
}

interface SessionListItemProps {
  session: ChatSession;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function SessionListItem({ session, isActive, onSelect, onDelete }: Readonly<SessionListItemProps>) {
  return (
    <li>
      <div
        className={cn(
          "group flex w-full items-center gap-1 pr-2 text-sm hover:bg-accent hover:text-accent-foreground",
          isActive && "bg-accent text-accent-foreground",
        )}
      >
        <button type="button" onClick={onSelect} className="flex min-w-0 flex-1 items-center gap-2 px-4 py-2.5 text-left">
          <MessageSquare className={cn("h-4 w-4 shrink-0", isActive ? "text-accent-foreground/80" : "text-muted-foreground")} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium">{session.title}</span>
              <StreamingDot sessionId={session.id} />
            </div>
            <div className={cn("flex items-center gap-1 truncate text-xs", isActive ? "text-accent-foreground/80" : "text-muted-foreground")}>
              <span className="truncate">{session.createdByEmail ?? "Unknown"}</span>
              <span>·</span>
              <span className="shrink-0">{formatRelativeAge(session.lastMessageAt ?? session.createdAt)}</span>
            </div>
          </div>
        </button>
        {session.isOwner && (
          <button
            type="button"
            onClick={onDelete}
            className="invisible shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive group-hover:visible focus:visible"
            aria-label={`Delete ${session.title}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}

// History marker for a session whose reply is still streaming; subscribes to just its boolean.
function StreamingDot({ sessionId }: Readonly<{ sessionId: string }>) {
  const streaming = useIsStreaming(sessionId);
  if (!streaming) return null;
  return (
    <span
      className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-brand-teal"
      aria-label="Response still generating"
      title="Response still generating"
    />
  );
}

interface ChatSessionViewProps {
  session: ChatSession;
  projectId: string;
  currentUserEmail: string | undefined;
  documents: EvidenceDocument[];
  totalDocs: number;
  documentsLoading: boolean;
  documentsByFilename: DocsByFilename;
  /** A question typed before the session existed (a new chat), sent once on mount. */
  firstQuestion?: string;
  onFirstQuestionSent?: () => void;
}

function ChatSessionView({
  session,
  projectId,
  currentUserEmail,
  documents,
  totalDocs,
  documentsLoading,
  documentsByFilename,
  firstQuestion,
  onFirstQuestionSent,
}: Readonly<ChatSessionViewProps>) {
  const { data: messages = [], isLoading } = useSessionMessages(session.id);
  const streamState = useChatStream(session.id);
  const { stream, cancel } = useStreamChatMessage(session.id, projectId);
  const { openDocument } = useDocumentViewer<EvidenceDocument>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const noDocuments = !documentsLoading && totalDocs === 0;

  const openCitation = useCallback<OpenCitation>(
    (citation) => {
      const doc = findDocBySourcefile(citation.sourcefile, documentsByFilename);
      if (!doc) return;
      const page = extractPageNumber(citation.sourcepage);
      // Non-modal, so the assistant stays usable beside the source.
      openDocument({ doc, page: page ?? undefined, sourceText: citation.content, side: "right", hideOverlay: true });
    },
    [documentsByFilename, openDocument],
  );

  // ── Stick-to-bottom auto-scroll: only follow new content when already at the bottom ──
  const stickToBottomRef = useRef(true);
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_THRESHOLD_PX;
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, streamState.streamingText, streamState.streamingThinking, streamState.pending]);

  /** Send a question over every document, or only the @-mentioned ones. False when it failed (keep the draft). */
  const send = useCallback(
    async (content: string): Promise<boolean> => {
      const mentionIds = parseAtMentionIds(content, documentsByFilename);
      let appliedFilters: AppliedFilters | null = null;
      if (mentionIds) {
        const ids = new Set(mentionIds);
        appliedFilters = {
          items: [{ key: "documents", label: "Mentioned", values: documents.filter((d) => ids.has(d.id)).map((d) => d.filename) }],
          matchedCount: ids.size,
          totalCount: totalDocs,
        };
      }
      stickToBottomRef.current = true;
      try {
        await stream(content, mentionIds ?? undefined, appliedFilters);
        return true;
      } catch {
        return false;
      }
    },
    [documentsByFilename, documents, totalDocs, stream],
  );

  // A new chat's first question, typed before this session existed. The ref survives StrictMode's double effect.
  const firstQuestionSent = useRef(false);
  useEffect(() => {
    if (!firstQuestion || firstQuestionSent.current) return;
    firstQuestionSent.current = true;
    onFirstQuestionSent?.();
    void send(firstQuestion);
  }, [firstQuestion, onFirstQuestionSent, send]);

  const hasStreamingThinking = streamState.streamingThinking.length > 0;
  // `stopped` keeps a halted answer on screen until the refetch brings the persisted interrupted row.
  const hasLiveAnswer = streamState.pending || streamState.stopped;
  const showStreamingBubble = hasLiveAnswer && streamState.streamingText.length > 0;
  const showWaiting = streamState.pending && streamState.streamingText.length === 0 && !hasStreamingThinking;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading messages…</div>
        ) : messages.length === 0 && !streamState.pending ? (
          <Greeting />
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                currentUserEmail={currentUserEmail}
                documentsByFilename={documentsByFilename}
                onOpenCitation={openCitation}
              />
            ))}
            {hasLiveAnswer && hasStreamingThinking && <ThinkingDisclosure text={streamState.streamingThinking} streaming />}
            {showStreamingBubble && (
              <AnswerCard route={streamState.route}>
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamState.streamingText}</ReactMarkdown>
                </div>
              </AnswerCard>
            )}
            {showWaiting && (
              <div className="flex items-center gap-2">
                <PulsingDots />
                {streamState.statusMessage && <span className="text-xs text-muted-foreground">{streamState.statusMessage}…</span>}
                {streamState.route && <RouteBadge route={streamState.route} />}
              </div>
            )}
            {streamState.error && <div className="text-sm text-destructive">{streamState.error}</div>}
          </div>
        )}
      </div>
      <Composer
        documentsByFilename={documentsByFilename}
        pending={streamState.pending}
        onStop={cancel}
        blockedReason={noDocuments ? "Add documents to the Evidence Base to start asking questions." : null}
        onSend={send}
      />
    </div>
  );
}

interface ComposerProps {
  documentsByFilename: DocsByFilename;
  /** A question is in flight: the input is locked and Send becomes Stop (when `onStop` is given). */
  pending: boolean;
  onStop?: () => void;
  /** Why nothing can be sent right now, shown in place of the hint; null when sending is allowed. */
  blockedReason: string | null;
  /** Resolves false when the question failed, to put it back in the input. */
  onSend: (content: string) => Promise<boolean>;
}

function Composer({ documentsByFilename, pending, onStop, blockedReason, onSend }: Readonly<ComposerProps>) {
  const [draft, setDraft] = useState("");
  const [atQuery, setAtQuery] = useState<string | null>(null);
  const [pickerIndex, setPickerIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  // Grow with the draft, up to the cap.
  useLayoutEffect(() => {
    const node = textareaRef.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [draft]);

  // ── @file picker ──
  const filteredFilenames = useMemo(() => {
    if (atQuery === null) return [];
    const q = atQuery.toLowerCase();
    return Array.from(documentsByFilename.keys())
      .sort((a, b) => a.localeCompare(b))
      .filter((name) => name.toLowerCase().includes(q))
      .slice(0, MAX_FILE_PICKER_RESULTS);
  }, [documentsByFilename, atQuery]);
  const pickerOpen = atQuery !== null && filteredFilenames.length > 0;

  const closePicker = useCallback(() => {
    setAtQuery(null);
    setPickerIndex(0);
  }, []);

  const handlePickFile = useCallback(
    (filename: string) => {
      const cursor = textareaRef.current?.selectionStart ?? draft.length;
      const atIdx = draft.slice(0, cursor).lastIndexOf("@");
      if (atIdx === -1) {
        closePicker();
        return;
      }
      const insertion = `@${filename} `;
      setDraft(draft.slice(0, atIdx) + insertion + draft.slice(cursor));
      closePicker();
      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        const pos = atIdx + insertion.length;
        node.focus();
        node.setSelectionRange(pos, pos);
      });
    },
    [draft, closePicker],
  );

  const handleSend = async () => {
    const content = draft.trim();
    if (!content || pending || blockedReason) return;
    setDraft("");
    closePicker();
    if (!(await onSend(content))) setDraft(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (pickerOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setPickerIndex((i) => (i + step + filteredFilenames.length) % filteredFilenames.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handlePickFile(filteredFilenames[pickerIndex]);
        return;
      }
      if (e.key === "Escape") {
        // Handled here, so the assistant window doesn't also close.
        e.preventDefault();
        closePicker();
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setDraft(next);
    setAtQuery(getAtQuery(next, e.target.selectionStart ?? next.length));
    setPickerIndex(0);
  };

  return (
    <div className="border-t px-3 pb-3 pt-2">
      <div className="mb-1.5 text-xs text-muted-foreground">
        {blockedReason ? (
          <span className="text-destructive">{blockedReason}</span>
        ) : pending ? (
          "Generating… you can close this window without losing the response"
        ) : (
          <>
            Use <span className="font-semibold text-brand-teal">@</span> to reference a document
          </>
        )}
      </div>
      <div ref={inputWrapperRef} className="relative flex items-end gap-2">
        {pickerOpen && (
          <FilePickerPopover
            filenames={filteredFilenames}
            activeIndex={pickerIndex}
            onSelect={handlePickFile}
            onClose={closePicker}
            anchorRef={inputWrapperRef}
          />
        )}
        <Textarea
          ref={textareaRef}
          value={draft}
          onChange={handleChange}
          onSelect={(e) => setAtQuery(getAtQuery(draft, e.currentTarget.selectionStart ?? draft.length))}
          onKeyDown={handleKeyDown}
          placeholder="Ask about evidence…"
          rows={1}
          className="min-h-[40px] resize-none py-2.5"
          disabled={pending}
          aria-label="Message"
        />
        {pending && onStop ? (
          <Button onClick={onStop} variant="outline" size="icon" className="h-10 w-10 shrink-0 rounded-full" aria-label="Stop generating" title="Stop generating">
            <Square className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={() => void handleSend()}
            disabled={!draft.trim() || pending || !!blockedReason}
            size="icon"
            aria-label="Send message"
            className="h-10 w-10 shrink-0 rounded-full bg-brand-teal text-white hover:bg-brand-teal/90"
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );
}

interface FilePickerPopoverProps {
  filenames: string[];
  activeIndex: number;
  onSelect: (filename: string) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

function FilePickerPopover({ filenames, activeIndex, onSelect, onClose, anchorRef }: Readonly<FilePickerPopoverProps>) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  // Outside click closes; a click on the textarea (to move the cursor) does not.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (listRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  return (
    <div
      ref={listRef}
      className="absolute bottom-full left-0 right-0 z-50 mb-2 max-h-64 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
    >
      <div className="border-b px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Project files</div>
      <ul>
        {filenames.map((name, i) => (
          <li key={name}>
            <button
              type="button"
              data-index={i}
              // mousedown, not click, so the textarea keeps focus.
              onMouseDown={(ev) => {
                ev.preventDefault();
                onSelect(name);
              }}
              className={cn("flex w-full items-center gap-2 px-3 py-2 text-left text-sm", i === activeIndex ? "bg-accent" : "hover:bg-accent/60")}
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">{name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const RouteBadge = ({ route }: { route: string }) => (
  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
    {route.toUpperCase()}
  </span>
);

const PulsingDots = () => (
  <div className="flex items-center gap-1 py-2" aria-label="Assistant is responding">
    <span className="h-2 w-2 animate-bounce rounded-full bg-brand-teal/60 [animation-delay:-0.3s]" />
    <span className="h-2 w-2 animate-bounce rounded-full bg-brand-teal/60 [animation-delay:-0.15s]" />
    <span className="h-2 w-2 animate-bounce rounded-full bg-brand-teal/60" />
  </div>
);

/** An assistant answer: a full-width teal-tinted card, as in the Evidence Base design. */
function AnswerCard({ route, title, children }: Readonly<{ route?: string | null; title?: string; children: React.ReactNode }>) {
  return (
    <div className="rounded-lg border border-brand-teal/15 bg-brand-teal/5 p-3" title={title}>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs font-semibold text-brand-teal">AI Assistant</span>
        {route && <RouteBadge route={route} />}
      </div>
      {children}
    </div>
  );
}

// Reasoning trace: open while streaming, collapsed on persisted turns.
function ThinkingDisclosure({ text, streaming }: Readonly<{ text: string; streaming?: boolean }>) {
  const [expanded, setExpanded] = useState(() => !!streaming);
  if (!text) return null;
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        {streaming ? <Loader2 className="h-3 w-3 animate-spin" /> : <Brain className="h-3 w-3" />}
        <span>{streaming ? "Thinking…" : "Thinking"}</span>
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {expanded && (
        <div className="mt-1 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-dashed px-3 py-2 text-xs italic text-muted-foreground">
          {text}
        </div>
      )}
    </div>
  );
}

interface AnchorRenderProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: React.ReactNode;
}

interface MessageBubbleProps {
  message: ChatMessage;
  currentUserEmail: string | undefined;
  documentsByFilename: DocsByFilename;
  onOpenCitation: OpenCitation;
}

function MessageBubble({ message, currentUserEmail, documentsByFilename, onOpenCitation }: Readonly<MessageBubbleProps>) {
  const isAssistant = message.role === "assistant";
  const label = senderLabel(message, currentUserEmail);
  const isOwnMessage = label === "You";

  const citationsById = useMemo(() => new Map((message.citations ?? []).map((c) => [String(c.id), c])), [message.citations]);

  // Inline [N] markers become #cite-N links, rendered as buttons that open the source (FR-3-05).
  const renderableContent = useMemo(
    () => (isAssistant && message.citations ? linkifyCitations(message.content) : message.content),
    [isAssistant, message.citations, message.content],
  );

  const markdownComponents = useMemo(
    () => ({
      a: ({ href, children, ...rest }: AnchorRenderProps) => {
        if (typeof href === "string" && href.startsWith(CITATION_HREF_PREFIX)) {
          const citation = citationsById.get(href.slice(CITATION_HREF_PREFIX.length));
          const resolved = !!citation && !!findDocBySourcefile(citation.sourcefile, documentsByFilename);
          return (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (citation) onOpenCitation(citation);
              }}
              disabled={!resolved}
              title={citation ? citationTitle(citation, resolved) : undefined}
              className={cn(
                "mx-0.5 inline-flex -translate-y-1 items-center rounded-sm px-1 align-baseline text-[10px] font-semibold leading-tight",
                resolved ? "bg-brand-teal/15 text-brand-teal hover:bg-brand-teal/25" : "cursor-not-allowed bg-muted text-muted-foreground",
              )}
            >
              {children}
            </button>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
            {children}
          </a>
        );
      },
    }),
    [citationsById, documentsByFilename, onOpenCitation],
  );

  if (isAssistant) {
    return (
      <div>
        {message.thinking && <ThinkingDisclosure text={message.thinking} />}
        <AnswerCard route={message.route} title={formatTime(message.createdAt)}>
          <div className="prose prose-sm max-w-none text-foreground dark:prose-invert">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {renderableContent}
            </ReactMarkdown>
          </div>
          {message.citations && message.citations.length > 0 && (
            <CitationChips citations={message.citations} documentsByFilename={documentsByFilename} onOpen={onOpenCitation} />
          )}
          {message.appliedFilters && <AppliedFiltersFooter applied={message.appliedFilters} />}
          {message.status === "interrupted" && (
            // A truncated answer must never read as a finished one.
            <div className="mt-2 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
              <Square className="h-3 w-3" />
              <span>Response interrupted — this answer is incomplete.</span>
            </div>
          )}
        </AnswerCard>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col", isOwnMessage ? "items-end" : "items-start")} title={formatTime(message.createdAt)}>
      {!isOwnMessage && <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>}
      <div className={cn("max-w-[85%] rounded-2xl px-3 py-2 text-sm", isOwnMessage ? "rounded-br-sm bg-brand-teal text-white" : "rounded-bl-sm bg-accent")}>
        <span className="whitespace-pre-wrap">{message.content}</span>
      </div>
    </div>
  );
}

function citationTitle(c: ChatCitation, resolved: boolean): string {
  if (!resolved) return `${c.sourcefile} is not in this project's documents`;
  const page = extractPageNumber(c.sourcepage);
  return `Open ${c.sourcefile}${page ? ` at page ${page}` : ""}`;
}

function CitationChips({
  citations,
  documentsByFilename,
  onOpen,
}: Readonly<{ citations: ChatCitation[]; documentsByFilename: DocsByFilename; onOpen: OpenCitation }>) {
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Sources">
      {citations.map((c) => {
        const resolved = !!findDocBySourcefile(c.sourcefile, documentsByFilename);
        const page = extractPageNumber(c.sourcepage);
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onOpen(c)}
            disabled={!resolved}
            title={citationTitle(c, resolved)}
            className={cn(
              "inline-flex max-w-full items-center gap-1 rounded px-2 py-0.5 text-xs",
              resolved ? "bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/20" : "cursor-not-allowed bg-muted text-muted-foreground",
            )}
          >
            <span className="font-semibold">[{c.id}]</span>
            <span className="truncate">{c.sourcefile}</span>
            {page !== null && page > 0 ? <span className="shrink-0">· p.{page}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function AppliedFiltersFooter({ applied }: Readonly<{ applied: AppliedFilters }>) {
  const { items, matchedCount, totalCount } = applied;
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      <span className="font-medium">Searched:</span>
      {items.map((item) => (
        <span
          key={item.key}
          title={`${item.label}: ${item.values.join(", ")}`}
          className="inline-flex items-center gap-1 rounded-full border border-brand-navy/30 bg-brand-navy/5 px-2 py-0.5 text-brand-navy"
        >
          <span className="font-medium">{item.label}:</span>
          <span className="max-w-[200px] truncate">{item.values.join(", ")}</span>
        </span>
      ))}
      <span>
        · {matchedCount} of {totalCount} documents
      </span>
    </div>
  );
}
